// @ts-strict-ignore
import * as React from 'react';
import {
    AppPlatform,
    ReleaseType,
    ReleaseStepType,
    AppStoreType,
    ReleaseTypesForNonBlockingSteps,
    ReleaseStepBlockingConfig,
} from '../types';
import Select from 'antd/lib/select';
import TreeSelect from 'antd/lib/tree-select';
import { RootState } from '../reducers/reducers';
import {
    selectReleaseStepsByReleaseType,
    selectWorkflowEnabledSteps,
    selectReleaseStepsBlockingConfigForReleaseType,
} from '../selectors/selectors';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { useSelector } from 'react-redux';
import { StepGraphStep } from '../types/workflow';

interface BlockingStepsSelectorProps {
    appId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stepGraphStepsMap?: Record<string, StepGraphStep>;
    releaseType?: ReleaseTypesForNonBlockingSteps;
    value?: string[];
    onChange?: (value: string[]) => void;
}

function useBlockingConfigMap(appId: string) {
    return useSelector((state: RootState) => selectReleaseStepsBlockingConfigForReleaseType(state, appId));
}

function isBlockingArchetype(
    blockingConfigMap: ReturnType<typeof selectReleaseStepsBlockingConfigForReleaseType>,
    releaseType: ReleaseTypesForNonBlockingSteps,
    archetype: ReleaseStepType,
): boolean {
    return blockingConfigMap[releaseType][archetype] === ReleaseStepBlockingConfig.Blocking;
}

function buildStepConfigTreeData(
    stepGraphStepsMap: Record<string, StepGraphStep>,
    blockingConfigMap: ReturnType<typeof selectReleaseStepsBlockingConfigForReleaseType>,
    workflowEnabledSteps: Record<string, boolean>,
    releaseType: ReleaseTypesForNonBlockingSteps,
    platform: AppPlatform,
    appStoreType: AppStoreType,
) {
    const blockingStepsByArchetype = new Map<ReleaseStepType, StepGraphStep[]>();
    for (const step of Object.values(stepGraphStepsMap)) {
        if (!isBlockingArchetype(blockingConfigMap, releaseType, step.archetype)) {
            continue;
        }
        const group = blockingStepsByArchetype.get(step.archetype) ?? [];
        group.push(step);
        blockingStepsByArchetype.set(step.archetype, group);
    }

    return Array.from(blockingStepsByArchetype.entries()).map(([archetype, steps]) => {
        const archetypeLabel = displayTextForReleaseStepType(
            archetype,
            platform,
            appStoreType,
            workflowEnabledSteps[archetype],
        );

        if (steps.length === 1) {
            return {
                title: archetypeLabel,
                value: steps[0].stepConfigId,
                key: steps[0].stepConfigId,
            };
        }

        return {
            title: archetypeLabel,
            value: `archetype:${archetype}`,
            key: `archetype:${archetype}`,
            selectable: false,
            children: steps.map((step) => ({
                title: step.stepName,
                value: step.stepConfigId,
                key: step.stepConfigId,
            })),
        };
    });
}

export const BlockingStepsSelector = ({
    appId,
    platform,
    appStoreType,
    stepGraphStepsMap,
    releaseType = ReleaseTypesForNonBlockingSteps.Standard,
    value,
    onChange,
}: BlockingStepsSelectorProps) => {
    const releaseStepsByReleaseType = useSelector((state: RootState) => selectReleaseStepsByReleaseType(state, appId));
    const allReleaseSteps =
        releaseStepsByReleaseType[
            releaseType === ReleaseTypesForNonBlockingSteps.Rollback ? ReleaseType.Rollback : ReleaseType.Minor
        ];
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const blockingConfigMap = useBlockingConfigMap(appId);

    const stepConfigTree = React.useMemo(() => {
        if (!stepGraphStepsMap) {
            return null;
        }
        return buildStepConfigTreeData(
            stepGraphStepsMap,
            blockingConfigMap,
            workflowEnabledSteps,
            releaseType,
            platform,
            appStoreType,
        );
    }, [stepGraphStepsMap, blockingConfigMap, workflowEnabledSteps, releaseType, platform, appStoreType]);

    if (stepConfigTree) {
        return (
            <TreeSelect
                treeCheckable
                showSearch
                style={{ width: '100%' }}
                value={value}
                onChange={(values) => onChange?.(values as string[])}
                treeData={stepConfigTree}
                placeholder="Select required steps"
                treeDefaultExpandAll
                allowClear
            />
        );
    }

    const archetypeOptions = allReleaseSteps
        .filter((step) => isBlockingArchetype(blockingConfigMap, releaseType, step))
        .map((step) => ({
            value: step,
            label: displayTextForReleaseStepType(step, platform, appStoreType, workflowEnabledSteps[step]),
        }));

    return (
        <Select
            mode="multiple"
            options={archetypeOptions}
            placeholder="Select required steps"
            value={value}
            onChange={onChange}
        />
    );
};
